/**
 * Accès DOM sûrs et détection du mode "mouvement réduit" pour le moteur de la vitrine.
 * Port de `$` (maquette:4162) et de la détection reduced-motion (mission-02 Partie B :331-354).
 * Source unique du mode mobile (`MOBILE_QUERY`, partagée par la feuille `styles/mobile.ts` et par le moteur).
 */

/** Override collant de `isReduced()` : `null` tant que `setReduced` n'a jamais été appelé. */
let override: boolean | null = null;

/**
 * Récupère un élément par id, ou lève si absent.
 * @param id identifiant DOM (sans le `#`)
 * @returns l'élément trouvé, typé `T`
 */
export function byId<T extends HTMLElement = HTMLElement>(id: string): T {
	const el = document.getElementById(id);
	if (!el) throw new Error(`#${id} absent`);
	return el as T; // le générique d'appel n'est pas vérifiable dynamiquement
}

/**
 * Récupère un élément via un sélecteur CSS sous `root`, ou lève si absent.
 * @param root racine de recherche
 * @param sel sélecteur CSS
 * @returns l'élément trouvé, typé `T`
 */
export function q<T extends Element = Element>(root: ParentNode, sel: string): T {
	const el = root.querySelector(sel);
	if (!el) throw new Error(`${sel} absent`);
	return el as T; // le générique d'appel n'est pas vérifiable dynamiquement
}

/**
 * Indique si le mode "mouvement réduit" est actif : priorité à l'override posé par
 * `setReduced`, sinon lecture (à chaque appel) de `prefers-reduced-motion`.
 */
export function isReduced(): boolean {
	if (override !== null) return override;
	if (typeof matchMedia !== "function") return false;
	return matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Pose un override collant pour `isReduced()` (utilisé par le mode debug figé). */
export function setReduced(v: boolean): void {
	override = v;
}

/**
 * Requête de bascule du mode mobile : même chaîne en CSS (`@media ${MOBILE_QUERY}`) et en JS
 * (`matchMedia`). Mobile = largeur < 940 px quelle que soit l'orientation, ou portrait jusqu'à
 * 1032 px (iPad Pro 13" portrait).
 */
export const MOBILE_QUERY = "(max-width: 939.98px), (orientation: portrait) and (max-width: 1032px)";

/** Variable CSS du facteur de réduction de la démonstration : écrite par `fitDemo` (`engine/demo-fit.ts`), lue par la règle D1 de `styles/mobile.ts`. */
export const DEMO_SCALE_VAR = "--demo-s";

/** Variable CSS de la hauteur réduite de la démonstration : écrite par `fitDemo` (`engine/demo-fit.ts`), lue par la règle D1 de `styles/mobile.ts`. */
export const DEMO_HEIGHT_VAR = "--demo-h";

let mobileList: MediaQueryList | null = null;
let mobileListSource: unknown = null; // identité du matchMedia qui a produit mobileList

/**
 * `MediaQueryList` de `MOBILE_QUERY`, mis en cache (les films l'interrogent à chaque image) et
 * recréé si l'identité du `matchMedia` global change (stubs de test). `null` sans `matchMedia` (SSR).
 */
function getMobileList(): MediaQueryList | null {
	if (typeof matchMedia !== "function") return null;
	if (mobileList === null || mobileListSource !== matchMedia) {
		mobileListSource = matchMedia;
		mobileList = matchMedia(MOBILE_QUERY);
	}
	return mobileList;
}

/** Le mode mobile est-il actif ? Lu à chaque appel (le `MediaQueryList` est vivant). */
export function isMobile(): boolean {
	return getMobileList()?.matches ?? false;
}

/**
 * S'abonne aux changements de mode mobile (rotation, redimensionnement).
 * @param cb appelé avec le nouvel état à chaque événement `change`
 * @returns fonction de désabonnement (inerte sans `matchMedia`)
 */
export function onMobileChange(cb: (mobile: boolean) => void): () => void {
	const list = getMobileList();
	if (!list) return () => {};
	const handler = (e: MediaQueryListEvent): void => cb(e.matches);
	list.addEventListener("change", handler);
	return () => list.removeEventListener("change", handler);
}
